import React, { useState, useEffect } from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import { alpha, styled } from "@mui/material/styles";
import MenuItem from "@mui/material/MenuItem";
import { Button, TablePagination, Typography, TextField } from "@mui/material";
import Grid from "@mui/material/Grid";
import moment from "moment";
import Modal from "../Modal";
import Toast from "../../../utils/toast";
import InputBase from "@mui/material/InputBase";
import IconButton from "@mui/material/IconButton";
import SearchIcon from "@mui/icons-material/Search";
import createInstance from "../../../api/axiosinstance";
// import COLORS from "../../Themes/Colours";
import { useThemeMode } from "../../Themes/ThemeContextTemp.js";

const aiDishaApi = createInstance("DISHA");

const roleCodes = {
  "Technical Leader": "TLE001",
  Manager: "MNG002",
  HR: "HRD003",
  Candidate: "CND004",
};
const CssTextField = styled(TextField)(({ theme }) => ({
  "& label.Mui-focused": {
    color: "#B9B9B9",
  },
  "& label": {
    color: "#FEFEFE",
  },
  "& .MuiInput-underline:after": {
    borderBottomColor: "#B9B9B9",
  },
  "& .MuiOutlinedInput-root": {
    "& fieldset": {
      borderColor: "#B9B9B9",
      borderRadius: 10,
    },
    "&:hover fieldset": {
      borderColor: "#B9B9B9",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#B9B9B9",
    },
    "& .MuiInputBase-input": {
      color: "#FEFEFE",
    },
  },
}));

const RoleManagement = () => {
  const [roles, setRoles] = useState([]);
  const [selectedRole, setRoleName] = useState("");
  const [selectedRoleDetails, setSelectedRoleDetails] = useState({});
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [deactivateId, setDeactiveId] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedStatus, setSelectedStatus] = useState(null);
  const { COLORS } = useThemeMode();

  useEffect(() => {
    aiDishaApi
      .get("/get-all-active-admin-roles/")
      .then((response) => {
        setRoles(response.data);
        console.log("admin roles", response.data);
      })
      .catch((error) => {
        console.error("Error fetching role data:", error);
      });
  }, []);

  const fetchActiveRoles = async () => {
    try {
      const response = await aiDishaApi.get("/get-all-admin-roles/");
      setRoles(response.data);
      console.log("Fetched data from fetch api", response.data);
    } catch (error) {
      console.error("Error fetching active roles:", error);
    }
  };

  // Call fetchActiveRoles when the component mounts
  useEffect(() => {
    fetchActiveRoles();
  }, []);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
  };
  const handleModalOpen = (role_id, status) => {
    setDeactiveId(role_id);
    setSelectedStatus(status);
    setIsModalOpen(true);
  };

  const handleConfirm = async (role_id) => {
    await handleDeactivateRole(role_id);
    setIsModalOpen(false);
  };

  const handleDeactivateRole = async (role_id, currentStatus) => {
    try {
      const newStatus = currentStatus === "Active" ? "Inactive" : "Active";

      const response = await aiDishaApi.patch(
        `/update-status-for-admin-roles/${role_id}/status?status=${newStatus}`,
      );

      if (response.status === 200) {
        Toast.success("Role Status successfully Updated");
        fetchActiveRoles();
      }
    } catch (error) {
      console.error("Error deactivating role:", error);
      Toast.error("Failed to deactivate role");
    }
  };

  const handleDropdownChange = (event) => {
    const roleName = event.target.value;
    setRoleName(roleName);
    const roleCode = roleCodes[roleName] || "";
    setSelectedRoleDetails({
      ...selectedRoleDetails,
      roleCode,
      roleId: roleName,
      creationdate: moment().format("DD/MM/YYYY"),
    });
  };
  const roless = [
    {
      role_id: "Role ID",
      name: "Role Name",
      role_code: "Role Code",
      creationdate: "Creation Date",
      action: "Action",
    },
  ];

  const rows = roles.map((role) => ({
    role_id: role.role_id,
    name: role.name,
    role_code: role.role_code,
    creationdate: role.creationdate
      ? moment(role.creationdate).format("DD/MM/YYYY")
      : "--",

    status: role.status,
  }));

  const filteredRows = rows.filter((row) =>
    row.name.toLowerCase().includes(searchTerm.toLowerCase()),
  );
  const paginatedRows = filteredRows.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );
  const handleClear = () => {
    setRoleName("");
    setSelectedRoleDetails({});
    setSearchTerm("");
  };

  const handleRoleSubmit = async () => {
    try {
      if (!selectedRole || !selectedRole.trim()) {
        console.error("Role name cannot be empty");
        Toast.error("Role name cannot be empty");
        return;
      }

      const existingRole = roles.find(
        (role) => role.name.toLowerCase() === selectedRole.toLowerCase(),
      );

      if (existingRole) {
        console.error("Role name already exists");
        Toast.error("Role name already exists");
        return;
      }

      // Make sure to pass role_name as a query parameter
      const response = await aiDishaApi.post("/create-admin-roles/", null, {
        params: {
          name: selectedRole,
          role_code: selectedRoleDetails.roleCode,
          role_id: selectedRoleDetails.roleId,
          creationdate: selectedRoleDetails.creationdate,
        },
      });

      if (response.status === 200) {
        Toast.success("Role submitted successfully");
        handleClear(); // Clear input fields after successful submission
        fetchActiveRoles(); // Refresh the state list
      } else {
        throw new Error("Failed to submit role data");
      }
    } catch (error) {
      console.error("Error while submitting role:", error);
      Toast.error("Failed to submit role data");
    }
  };

  return (
    <div>
      <Box
        display="grid"
        gridTemplateColumns="repeat(12, 1fr)"
        sx={{
          marginTop: "30px",
          alignItems: "center",
          // background: alpha("#B9B9B9", 0.2),
          background: COLORS.primary,
          borderRadius: "10px",
          padding: "15px",
        }}
      >
        <Box gridColumn="span 6" sx={{ marginTop: "auto", textAlign: "left" }}>
          <Typography
            sx={{ fontFamily: "poppins", fontSize: "28px", color: COLORS.text}}
          >
            Add Role
          </Typography>
          <Typography
            sx={{ fontFamily: "poppins", fontSize: "16px", color:COLORS.text }}
          >
            Add the Name of the Role
          </Typography>
        </Box>
        <Box
          gridColumn="span 6"
          sx={{ marginTop: "auto", textAlign: "left", marginBottom: "2%" }}
        >
          <Paper
            sx={{
              p: "2px 4px",
              display: "flex",
              width: "75%",
              borderRadius: 2,
              backgroundColor: COLORS.background,
            }}
          >
            <IconButton
              type="button"
              sx={{ p: "8px", color: COLORS.text }}
              aria-label="search"
            >
              <SearchIcon />
            </IconButton>
            <InputBase
              sx={{ ml: 1, flex: 1, 
                fontFamily: "Poppins", 
                color: COLORS.text,
              "& input::placeholder": {
                 color: `${COLORS.text} !important`, // 👈 placeholder color
                 opacity: 10, // important (MUI reduces opacity by default)
               }, }}
              placeholder="Search by Role Name"
              inputProps={{ "aria-label": "search google maps" }}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)} // Update search term
            />
          </Paper>
        </Box>

        <Box gridColumn="span 4" sx={{ marginTop: "auto", textAlign: "left" }}>
          <Typography sx={{ fontFamily: "poppins", color: COLORS.text }}>
            Role Name
          </Typography>
          <CssTextField
            select // Add this prop to make it a select dropdown
            label="Role Name"
            value={selectedRole}
            onChange={handleDropdownChange}
            size="small"
            sx={{
              background:COLORS.background,
              borderRadius:2,
              width: "95%",
               "& .MuiOutlinedInput-root": {
                "& .MuiSelect-select": {
                  color: COLORS.text, // input text color
                },
                "&.Mui-disabled fieldset": {
                  borderColor: COLORS.border, // border color for disabled
                },
                "&.Mui-disabled input": {
                  WebkitTextFillColor: COLORS.text, // 👈 fix for disabled text color
                },
              },
              "& .MuiInputLabel-root": {
                color: COLORS.text, // label color
              },
              "& .MuiInputLabel-root.Mui-disabled": {
                color: COLORS.text, // label color when disabled
              },
             }}
            required
          >
            <MenuItem value="" disabled sx={{ backgroundColor: COLORS.background }}>
              Select Role
            </MenuItem>
            {Object.keys(roleCodes).map((roleName) => (
              <MenuItem
                key={roleName}
                value={roleName}
                sx={{ background: COLORS.background, color: COLORS.text }}
              >
                {roleName}
              </MenuItem>
            ))}
          </CssTextField>
        </Box>

        <Box gridColumn="span 4" sx={{ marginTop: "auto", textAlign: "left" }}>
          <Typography sx={{ fontFamily: "poppins", color: COLORS.text }}>
            Role Code
          </Typography>
          <CssTextField
            disabled
            label="Role Code"
            id="custom-css-outlined-input"
            value={selectedRoleDetails.roleCode || ""}
            size="small"
            sx={{
              background:COLORS.background,
              borderRadius:2,
              width: "95%",
              "& .MuiOutlinedInput-root": {
                "& input": {
                  color: COLORS.text, // input text color
                },
                "&.Mui-disabled fieldset": {
                  borderColor: COLORS.border, // border color for disabled
                },
                "&.Mui-disabled input": {
                  WebkitTextFillColor: COLORS.text, // 👈 fix for disabled text color
                },
              },
              "& .MuiInputLabel-root": {
                color: COLORS.text, // label color
              },
              "& .MuiInputLabel-root.Mui-disabled": {
                color: COLORS.text, // label color when disabled
              },
            }}
          />

          {/* <CssTextField
            disabled
            label="Role Code"
            id="custom-css-outlined-input"
            value={selectedRoleDetails.roleCode || ""}
            size="small"
            sx={{ width: "95%", 
               "& .MuiOutlinedInput-root": {
                   "& input": {
                   color: "white", // 👈 input text color
                 },
               },

             }}
          /> */}
        </Box>

        <Box
          gridColumn="span 2"
          sx={{ marginTop: "auto", ml: "15%", textAlign: "left" }}
        >
          <Button
            onClick={handleClear}
            sx={{
              border: "1px solid",
              color: COLORS.text,
              "&hover":{
              color:COLORS.hover_btn
              },
              width: "120px",
              height: "40px",
              borderRadius: "10px",
              fontFamily: "poppins",
            }}
          >
            Clear
          </Button>
        </Box>

        <Box gridColumn="span 2" sx={{ marginTop: "auto", textAlign: "left" }}>
          <Button
            onClick={handleRoleSubmit}
            sx={{
              background: COLORS.button,
              "&:hover":{
              background:COLORS.hover_btn
              },
              width: "120px",
              height: "40px",
              borderRadius: "10px",
              fontFamily: "poppins",
            }}
          >
            <Typography sx={{ fontFamily: "poppins", color: COLORS.background }}>
              Save
            </Typography>
          </Button>
        </Box>
      </Box>

      <Box xs={1} sx={{ backgroundColor: "#33333" }}>
        <Grid container spacing={1} sx={{ justifyContent: "center" }}>
          <Grid item xs={10}>
            <TableContainer
              component={Paper}
              sx={{
                marginTop: "25px",
                borderRadius: "10px",
              }}
            >
              <Table size="small">
                <TableHead sx={{ background: COLORS.primary }}>
                  {roless.map((role) => (
                    <TableRow key={role.id}>
                      <TableCell sx={{ color: COLORS.text, borderBottom: 0 }}>
                        {role.role_id}
                      </TableCell>
                      <TableCell sx={{ color: COLORS.text, borderBottom: 0 }}>
                        {role.name}
                      </TableCell>
                      <TableCell sx={{ color: COLORS.text, borderBottom: 0 }}>
                        {role.role_code}
                      </TableCell>
                      <TableCell sx={{ color: COLORS.text, borderBottom: 0 }}>
                        {role.creationdate}
                      </TableCell>
                      <TableCell sx={{ color: COLORS.text, borderBottom: 0 }}>
                        {role.action}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableHead>
                <TableBody>
                  {paginatedRows.map((row) => {
                    //  console.log("checking roles row: ",row)
                    return (
                      <TableRow
                        key={row.role_id}
                        sx={{ background: COLORS.background, marginRight: "30px" }}
                      >
                        <TableCell
                          sx={{ color: COLORS.text, border: COLORS.secondary}}
                        >
                          {row.role_id}
                        </TableCell>
                        <TableCell
                          sx={{ color: COLORS.text, border: COLORS.secondary }}
                        >
                          {row.name}
                        </TableCell>
                        <TableCell
                          sx={{ color: COLORS.text, border: COLORS.secondary }}
                        >
                          {row.role_code}
                        </TableCell>
                        <TableCell
                          sx={{ color: COLORS.text, border: COLORS.secondary }}
                        >
                          {row.creationdate}
                        </TableCell>
                        <TableCell
                          sx={{ color: COLORS.text, border: COLORS.secondary }}
                        >
                          <Button
                            variant="contained"
                            sx={{
                              backgroundColor:
                                row.status === "Active" ? COLORS.button_red_bg : COLORS.button_green_bg,
                              color: COLORS.text,
                              fontFamily: "poppins",
                              height: "24px",
                              padding: "2px 8px",
                              minWidth: "90px",
                              fontSize: "0.75rem",
                              "&:hover": {
                              background:
                                row.status === "Active"
                                  ? COLORS.hover_red_btn      // hover when Active (Deactivate button)
                                  : COLORS.hover_green_btn,   // hover when Inactive (Activate button)
                            },
                            }}
                            onClick={() => {
                              console.log(
                                "Deactivate button clicked for:",
                                row.dept_id,
                                row.status,
                              );
                              handleModalOpen(row.role_id, row.status);
                            }}
                            // sx={{
                            //   color: "white",
                            //   marginLeft: "20px",
                            //   fontFamily: "poppins",
                            //   height: "20px",
                            //   width: "100px",
                            // }}
                            // onClick={() => handleModalOpen(row.role_id)}
                            // color="error"
                          >
                            {row.status === "Active"
                              ? "Deactivate"
                              : "Activate"}
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={filteredRows.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                sx={{
                  background: COLORS.primary,
                  color: COLORS.text,
                }}
              />
            </TableContainer>
          </Grid>
        </Grid>
        {/* <Modal open={isModalOpen} onClose={handleModalClose}>
            <Typography>Are you sure you want to deactivate this state?</Typography>
            <Button onClick={handleConfirm}>Confirm</Button>
            <Button onClick={handleModalClose}>Cancel</Button>
          </Modal> */}
      </Box>

      <Modal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        title="Role"
        action={() => handleDeactivateRole(deactivateId, selectedStatus)}
        actionType={selectedStatus === "Active" ? "Deactivate" : "Activate"}
      />
    </div>
  );
};

export default RoleManagement;
